import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorState } from '@/components/ui/error-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { apiJson } from '@/lib/api';

type WorkTaskType = 'POSTING' | 'REMOVAL' | 'REPOST';
type WorkKind = 'all' | 'installs' | 'removals';

interface WorkCycle {
  id: number;
  name: string;
  startDate: string | null;
  installCount: number;
  removalCount: number;
}

interface WorkTask {
  id: number;
  taskType: WorkTaskType;
  status: string;
  targetQuantity: number;
  updatedAt: string | null;
  campaignName: string;
  cycleId: number | null;
  cycleName: string | null;
  operatorName: string | null;
  taskName: string | null;
  packName: string | null;
}

interface StoreWorkHistory {
  cycles: WorkCycle[];
  tasks: WorkTask[];
}

const STATUS_LABELS: Record<string, string> = {
  operator_complete: 'Operator complete',
  admin_complete: 'Signed off',
  sent_to_operator: 'With operator',
  pending: 'Pending',
  admin_pending_review: 'In review',
  admin_rejected: 'Rejected',
  locked: 'Locked',
};

const KIND_FILTERS: Array<{ id: WorkKind; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'installs', label: 'Installs' },
  { id: 'removals', label: 'Removals' },
];

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

function taskKindLabel(taskType: WorkTaskType): string {
  switch (taskType) {
    case 'POSTING':
      return 'Install';
    case 'REPOST':
      return 'Repost';
    case 'REMOVAL':
      return 'Removal';
    default: {
      const exhaustive: never = taskType;
      return exhaustive;
    }
  }
}

function matchesKind(taskType: WorkTaskType, kind: WorkKind): boolean {
  switch (kind) {
    case 'all':
      return true;
    case 'installs':
      return taskType === 'POSTING' || taskType === 'REPOST';
    case 'removals':
      return taskType === 'REMOVAL';
    default: {
      const exhaustive: never = kind;
      return exhaustive;
    }
  }
}

function formatWhen(value: string | null): string {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No date';
  return format(date, 'd MMM yyyy');
}

function useStoreWorkHistory(storeId: number) {
  return useQuery({
    queryKey: ['store-work-history', storeId],
    queryFn: () => apiJson<StoreWorkHistory>(`/api/stores/${storeId}/work-history`),
  });
}

export function StoreTaskHistory({ storeId }: { storeId: number }) {
  const { data, isLoading, isError, error } = useStoreWorkHistory(storeId);
  const [cycleId, setCycleId] = useState<number | null>(null);
  const [kind, setKind] = useState<WorkKind>('all');

  useEffect(() => {
    if (!data) return;
    setCycleId((current) => {
      if (current != null && data.cycles.some((cycle) => cycle.id === current)) return current;
      return data.cycles[0]?.id ?? null;
    });
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" aria-label="Loading history" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <ErrorState
        title="Could not load history"
        message={error instanceof Error ? error.message : 'Installs and removals are unavailable.'}
        showRetry={false}
      />
    );
  }

  const selectedCycleId = cycleId ?? data.cycles[0]?.id ?? null;
  const cycleTasks = data.tasks.filter((task) => selectedCycleId == null || task.cycleId === selectedCycleId);
  const visible = cycleTasks.filter((task) => matchesKind(task.taskType, kind));
  const installCount = cycleTasks.filter((task) => matchesKind(task.taskType, 'installs')).length;
  const removalCount = cycleTasks.filter((task) => matchesKind(task.taskType, 'removals')).length;
  const selectedCycle = data.cycles.find((cycle) => cycle.id === selectedCycleId) ?? null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Store history</CardTitle>
        <p className="text-sm text-gray-500">
          Installs and removals for this store, one cycle at a time.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={selectedCycleId == null ? undefined : String(selectedCycleId)}
            onValueChange={(value) => setCycleId(Number(value))}
            disabled={data.cycles.length === 0}
          >
            <SelectTrigger className="w-64" aria-label="Cycle">
              <SelectValue placeholder={data.cycles.length === 0 ? 'No cycles' : 'Cycle'} />
            </SelectTrigger>
            <SelectContent>
              {data.cycles.map((cycle) => (
                <SelectItem key={cycle.id} value={String(cycle.id)}>
                  {cycle.name}
                  {cycle.startDate ? ` · ${formatWhen(cycle.startDate)}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {KIND_FILTERS.map((filter) => {
            const count = filter.id === 'installs'
              ? installCount
              : filter.id === 'removals'
                ? removalCount
                : cycleTasks.length;
            const isActive = kind === filter.id;
            return (
              <Button
                key={filter.id}
                type="button"
                size="sm"
                variant={isActive ? 'default' : 'outline'}
                onClick={() => setKind(filter.id)}
                aria-pressed={isActive}
              >
                {filter.label}
                <span className="ml-1.5 tabular-nums">{count}</span>
              </Button>
            );
          })}
        </div>

        {selectedCycle ? (
          <p className="text-sm text-gray-500">
            {selectedCycle.name}: {installCount} install{installCount === 1 ? '' : 's'}, {removalCount} removal{removalCount === 1 ? '' : 's'}.
          </p>
        ) : null}

        {visible.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            {data.tasks.length === 0
              ? 'No installs or removals recorded for this store.'
              : 'Nothing in this cycle matches the filter.'}
          </p>
        ) : (
          <ol className="relative ml-3 space-y-4 border-l-2 border-gray-200 pl-6">
            {visible.map((task) => (
              <li key={task.id} className="relative">
                <span className="absolute -left-[1.85rem] h-3.5 w-3.5 rounded-full border-2 border-blue-400 bg-white" aria-hidden />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs tabular-nums text-gray-500">{formatWhen(task.updatedAt)}</span>
                  <Badge variant="outline" className="text-[10px]">{taskKindLabel(task.taskType)}</Badge>
                  <Badge variant="secondary" className="text-[10px]">{statusLabel(task.status)}</Badge>
                </div>
                <p className="mt-0.5 text-sm font-medium">{task.campaignName}</p>
                <p className="text-sm text-gray-600">
                  {[task.packName, task.taskName, task.operatorName].filter(Boolean).join(' · ')}
                  {task.targetQuantity > 0 ? ` · ${task.targetQuantity}` : ''}
                </p>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
